import { PrintedLanguage } from "../MathFactsGenerator/PrintedLanguage.ts";

/**
 * The shared PDF chrome (ADR 0051 §7/§Part-3): "Name:", "Date:" and the rest
 * of the header/footer furniture every format prints, keyed off `Language` -
 * not the interface locale. A Spanish worksheet printed from an English
 * interface must still print Spanish chrome, and an English worksheet
 * printed from a Spanish interface (once one exists) must still print
 * English chrome. That is why this is a plain lookup taking `Language`
 * directly, rather than `useTranslation()`'s interface-locale hook.
 *
 * "Math-Pages.com" is a domain name, not translated in any locale.
 */
export interface PdfChromeStrings {
    name: string;
    date: string;
    time: string;
    score: string;
    /** "Answers, page {page} of {total}" on a re-flowed worked-answer key. */
    answersPage: (page: number, total: number) => string;
    /** The heading of the name column on the fact fluency tracking chart (ADR 0071 §8), no colon. */
    chartName: string;
    /** "{ladder} · tracking chart": the chart's title, given the ladder's own title. */
    chartTitle: (ladder: string) => string;
    /** Where a parent signs a home practice sheet (ADR 0071 §8), in place of the date. */
    signed: string;
    /**
     * "Level {n} · {name}" on a fact fluency level test and its key (ADR 0071 §4); with
     * `practice`, "Level {n} · {name} · Home practice". The words go in the label under the
     * row, not in the row, which has no width to spare.
     */
    level: (n: number, name: string, practice?: boolean) => string;
}

const CHROME: Record<PrintedLanguage, PdfChromeStrings> = {
    en: {
        name: "Name:",
        date: "Date:",
        time: "Time:",
        score: "Score:",
        signed: "Signed:",
        chartName: "Name",
        chartTitle: (ladder) => `${ladder} · tracking chart`,
        answersPage: (page, total) => `Answers, page ${page} of ${total}`,
        level: (n, name, practice) => `Level ${n} · ${name}` + (practice ? " · Home practice" : ""),
    },
    es: {
        name: "Nombre:",
        date: "Fecha:",
        time: "Tiempo:",
        score: "Puntaje:",
        signed: "Firma:",
        chartName: "Nombre",
        chartTitle: (ladder) => `${ladder} · tabla de seguimiento`,
        answersPage: (page, total) => `Respuestas, página ${page} de ${total}`,
        level: (n, name, practice) => `Nivel ${n} · ${name}` + (practice ? " · Práctica en casa" : ""),
    },
    fr: {
        name: "Nom :",
        date: "Date :",
        time: "Temps :",
        score: "Score :",
        signed: "Signé :",
        chartName: "Nom",
        chartTitle: (ladder) => `${ladder} · tableau de suivi`,
        answersPage: (page, total) => `Réponses, page ${page} sur ${total}`,
        level: (n, name, practice) => `Niveau ${n} · ${name}` + (practice ? " · Entraînement à la maison" : ""),
    },
};

export function pdfChrome(language: PrintedLanguage | undefined): PdfChromeStrings {
    return CHROME[language ?? "en"];
}
